import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Link from 'flarum/common/components/Link';
const t = (key, params) => app.translator.trans('ppk2-profile.forum.agreement.' + key, params);

/** GET /api/ppk2/agreement (shared by both windows, reloaded when asked). */
export function loadAgreement() {
  return app.request({
    method: 'GET',
    url: app.forum.attribute('apiUrl') + '/ppk2/agreement'
  }).then(r => r.data);
}

/**
 * «Пользовательское соглашение» window (from the registration form).
 * Texts come from the server: mode «Свой текст» = one text, no language picker;
 * mode «Тема соглашения» = one text per LANG=… post, with its own language picker
 * (independent of the forum language; default English, else the first one).
 * attrs.lang: language to show first; attrs.onlang(code): called when the language changes.
 */
export default class AgreementModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    this.data = null;
    this.error = false;
    this.lang = this.attrs.lang || null;
    this.load();
  }
  load() {
    this.data = null;
    this.error = false;
    loadAgreement().then(data => {
      this.data = data;
      if (!this.lang || !data.html[this.lang]) this.lang = data.defaultLang;
      if (this.attrs.onlang) this.attrs.onlang(this.lang, data.edition);
      m.redraw();
    }, () => {
      this.error = true;
      m.redraw();
    });
  }
  className() {
    return 'Ppk2Agreement';
  }
  title() {
    return t('title');
  }
  picker() {
    const d = this.data;
    if (!d || d.mode !== 'topic' || !d.languages.length) return null;
    return m("div", {
      className: "Ppk2Agreement-lang"
    }, m("label", {
      for: "ppk2-agreement-lang"
    }, m("i", {
      className: "fas fa-language",
      "aria-hidden": "true"
    }), " ", t('language')), m("select", {
      id: "ppk2-agreement-lang",
      className: "FormControl",
      value: this.lang,
      onchange: e => {
        this.lang = e.target.value;
        if (this.attrs.onlang) this.attrs.onlang(this.lang, d.edition);
      }
    }, d.languages.map(l => m("option", {
      value: l.code
    }, l.name || l.code))), m("span", {
      className: "Ppk2Agreement-edition"
    }, t('edition', {
      n: d.edition
    })));
  }
  text() {
    if (this.error) return m("p", {
      className: "Ppk2Agreement-error"
    }, t('load_error'));
    if (!this.data) return m(LoadingIndicator, null);
    const html = this.data.html[this.lang] || '';
    return m("article", {
      className: "Ppk2Agreement-text Post-body",
      lang: this.lang === 'custom' ? null : this.lang
    }, m.trust(html));
  }
  intro() {
    return null;
  }
  buttons() {
    return m("div", {
      className: "Ppk2Agreement-buttons"
    }, m(Button, {
      className: "Button Button--primary",
      onclick: () => this.hide()
    }, t('close')));
  }
  content() {
    return m("div", {
      className: "Modal-body"
    }, this.intro(), this.picker(), this.text(), this.buttons());
  }
}

/**
 * Shown on the forum (not in the admin panel) when the agreement changed since the player's
 * last acceptance, or was switched on and they never accepted it. Cannot be closed:
 * «Принимаю» (POST /api/ppk2/agreement/accept) or «Покинуть портал» (log out).
 * The page «Безопасность и удаление» stays usable (export / delete data) without the window.
 */
export class ReacceptModal extends AgreementModal {
  static isDismissibleViaCloseButton = false;
  static isDismissibleViaEscKey = false;
  static isDismissibleViaBackdropClick = false;
  oninit(vnode) {
    super.oninit(vnode);
    this.saving = false;
    this.first = !this.attrs.everAccepted;
  }
  className() {
    return 'Ppk2Agreement Ppk2Reaccept';
  }
  title() {
    return this.first ? t('first_title') : t('reaccept_title');
  }
  intro() {
    return m("p", {
      className: "Ppk2Reaccept-intro"
    }, this.first ? t('first_intro') : t('reaccept_intro'));
  }
  buttons() {
    const user = app.session.user;
    return [m("p", {
      className: "Ppk2Reaccept-note helpText"
    }, t('leave_note', {
      a: m(Link, {
        href: user ? app.route('user.security', {
          username: user.slug()
        }) : '#',
        onclick: () => this.hide()
      })
    })), m("div", {
      className: "Ppk2Agreement-buttons Ppk2Reaccept-buttons"
    }, m(Button, {
      className: "Button Ppk2Reaccept-leave",
      icon: "fas fa-sign-out-alt",
      disabled: this.saving,
      onclick: () => app.session.logout()
    }, t('leave')), m(Button, {
      className: "Button Button--primary Ppk2Reaccept-accept",
      icon: "fas fa-check",
      loading: this.saving,
      disabled: !this.data,
      onclick: () => this.accept()
    }, t('accept')))];
  }
  accept() {
    this.saving = true;
    app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/ppk2/agreement/accept',
      body: {
        edition: this.data.edition,
        lang: this.lang
      },
      errorHandler: () => {}
    }).then(() => {
      app.forum.pushAttributes({
        ppk2AgreementPending: false
      });
      this.hide();
      app.alerts.show({
        type: 'success'
      }, t('accepted'));
    }, e => {
      // the text changed meanwhile (422): show the new edition
      app.alerts.show({
        type: 'error'
      }, e.response && e.response.errors && e.response.errors[0] && e.response.errors[0].detail || 'Error');
      this.load();
    }).finally(() => {
      this.saving = false;
      m.redraw();
    });
  }
}
flarum.reg.add('ppk2-profile', 'forum/signup/AgreementModal', AgreementModal);